
import React, { useMemo } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { calculateMortgagePayment } from '../utils/mortgageUtils';
import type { MortgageData } from '../../CalculatorTabs';

interface CostScenariosTabProps {
  selectedTerm: string;
  mortgageData: MortgageData;
}

export const CostScenariosTab: React.FC<CostScenariosTabProps> = ({ selectedTerm, mortgageData }) => {
  const costScenariosData = useMemo(() => {
    const { homePrice, downPayment, interestRate, homeInsurance, propertyTax, closingCosts } = mortgageData;
    const termYears = parseInt(selectedTerm);
    const loanAmount = homePrice - downPayment;
    
    // Calculate monthly taxes and insurance properly
    const monthlyPropertyTax = propertyTax / 12;
    const monthlyInsurance = homeInsurance / 12;
    const monthlyTaxesAndInsurance = monthlyPropertyTax + monthlyInsurance;
    
    // Current plan
    const currentMonthlyPI = calculateMortgagePayment(loanAmount, interestRate, termYears);
    const currentMonthlyTotal = currentMonthlyPI + monthlyTaxesAndInsurance;
    
    // 20% down scenario
    const twentyDownAmount = homePrice * 0.2;
    const twentyDownLoan = homePrice - twentyDownAmount;
    const twentyDownMonthlyPI = calculateMortgagePayment(twentyDownLoan, interestRate, termYears);
    const twentyDownMonthlyTotal = twentyDownMonthlyPI + monthlyTaxesAndInsurance;
    
    // Different term scenario
    const altTermYears = termYears === 15 ? 30 : 15;
    const altTermMonthlyPI = calculateMortgagePayment(loanAmount, interestRate, altTermYears);
    const altTermMonthlyTotal = altTermMonthlyPI + monthlyTaxesAndInsurance;
    
    return [
      {
        name: 'Current Plan',
        monthlyPayment: Math.round(currentMonthlyTotal),
        upfrontCost: Math.round(downPayment + closingCosts.total),
        totalCost: Math.round(currentMonthlyTotal * termYears * 12 + downPayment + closingCosts.total)
      },
      {
        name: '20% Down',
        monthlyPayment: Math.round(twentyDownMonthlyTotal),
        upfrontCost: Math.round(twentyDownAmount + closingCosts.total),
        totalCost: Math.round(twentyDownMonthlyTotal * termYears * 12 + twentyDownAmount + closingCosts.total)
      },
      {
        name: `${altTermYears}-Year Loan`,
        monthlyPayment: Math.round(altTermMonthlyTotal),
        upfrontCost: Math.round(downPayment + closingCosts.total),
        totalCost: Math.round(altTermMonthlyTotal * altTermYears * 12 + downPayment + closingCosts.total)
      }
    ];
  }, [selectedTerm, mortgageData]);

  return (
    <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-6">
      <h3 className="text-lg font-bold text-slate-100 mb-4">
        Cost Comparison Scenarios ({selectedTerm} Year Base Term)
      </h3>
      <div className="w-full h-96">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={costScenariosData}>
            <XAxis 
              dataKey="name" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#94a3b8', fontSize: 12 }}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              tickFormatter={(value) => `$${(value / 1000)}k`}
            />
            <Tooltip
              contentStyle={{ 
                backgroundColor: '#0f172a', 
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#e2e8f0'
              }}
              formatter={(value, name) => {
                const label = name === 'monthlyPayment' ? 'Monthly Payment' : 
                             name === 'upfrontCost' ? 'Upfront Cost' : 'Total Cost';
                return [`$${Number(value).toLocaleString()}`, label];
              }}
            />
            <Bar dataKey="totalCost" fill="#f59e0b" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
